import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import utils from 'utils';
import useActions from 'hooks/useActions';
import { getAlamoContactUsSupportPhoneNumber } from 'selectors/supportSelectors';
import { getSupportContactInfo } from 'actions/content';
import CUSTOMER_SUPPORT from 'constants/customerSupport';
import { isFetchingEndpointSelector } from 'selectors/fetchingSelectors';
import { getPickupLocationCountryCode } from 'selectors/reservationSelectors';

const { PHONE_TYPE } = CUSTOMER_SUPPORT;

/**
 * useSupportPhoneNumber - Get support phone number based on pickup location OR country domain
 * @return {string} support phone number
 */
const useSupportPhoneNumber = () => {
  const domainCountry = utils.config.getDomainCountry();
  const pickupLocationCountryCode = useSelector(getPickupLocationCountryCode);
  const getSupportContactInfoHandler = useActions(getSupportContactInfo);

  // If return location country code is available, use it to get support phone number. Otherwise, use domain country.
  const domainSupportPhone = useSelector((state) =>
    getAlamoContactUsSupportPhoneNumber(state, domainCountry)
  )?.phone_number;
  const pickupLocationSupportPhone = useSelector((state) =>
    pickupLocationCountryCode !== domainCountry
      ? getAlamoContactUsSupportPhoneNumber(state, pickupLocationCountryCode)
      : null
  )?.phone_number;

  const isFetchingContactInfo = useSelector((state) =>
    isFetchingEndpointSelector(state, { endpoint: 'support/contact' })
  );
  const [supportPhoneVal, setsupportPhoneVal] = useState(domainSupportPhone);
  const [pickupLocationSupportPhoneVal, setPickupLocationSupportPhoneVal] = useState(pickupLocationSupportPhone);

  const dispatchReduxAction = async (countryCode, supportPhone) => {
    // Retrieve Support Contact if no support phone available.
    if (!supportPhone && !isFetchingContactInfo) {
      await getSupportContactInfoHandler(countryCode).then((data) => {
        const phoneType = PHONE_TYPE.CONTACT_US;
        const contactPhone = data?.support_phone_numbers?.find((phoneNum) => phoneNum?.phone_type === phoneType);

        if (contactPhone?.phone_number) {
          setsupportPhoneVal(contactPhone?.phone_number);
          setPickupLocationSupportPhoneVal(contactPhone?.phone_number);
        }
      });
    }
  };

  useEffect(() => {
    dispatchReduxAction(domainCountry, domainSupportPhone);
    // fetching support contact details only if pickup location is differ from domain country code
    if (pickupLocationCountryCode && pickupLocationCountryCode !== domainCountry) {
      dispatchReduxAction(pickupLocationCountryCode, pickupLocationSupportPhone);
    }
  }, [domainCountry, isFetchingContactInfo]);

  return { domainSupportPhoneNumber: supportPhoneVal, pickupLocationSupportPhoneNumber: pickupLocationSupportPhoneVal };
};

export default useSupportPhoneNumber;
